import { useTranslation } from "react-i18next";
import { ERROR_CODES } from "@nh/shared";
import { isApiError } from "@/shared/api/client";

/**
 * El texto que se le enseña a una persona cuando el API de paquetes
 * falla.
 *
 * Se ramifica sobre `code`, NUNCA sobre el texto del mensaje: el mensaje
 * del servidor puede cambiar de redacción o venir en otro idioma, y un
 * `if (message.includes("no encontrado"))` se rompe sin que nada lo
 * avise.
 *
 * Es gemelo del de productos y no una importación suya porque lo único
 * que cambia entre los dos es justo lo que importa: qué se le dice a
 * quien está mirando. "Ese producto ya no existe" en la pantalla de
 * paquetes manda a buscar el problema al sitio equivocado.
 */
export function useApiErrorMessage(error: unknown): string {
  const { t } = useTranslation();
  if (!error) return "";

  if (!isApiError(error)) {
    return t("Algo salió mal. Vuelve a intentarlo.");
  }

  switch (error.code) {
    case ERROR_CODES.FORBIDDEN:
      return t("Tu cuenta no tiene permiso para esta acción.");
    case ERROR_CODES.UNAUTHENTICATED:
      return t("Tu sesión expiró. Vuelve a entrar.");
    case ERROR_CODES.NOT_FOUND:
      return t("Ese paquete ya no existe.");
    case ERROR_CODES.CONFLICT:
      return t("Ya existe un paquete con ese slug.");
    default:
      return error.message || t("Algo salió mal. Vuelve a intentarlo.");
  }
}
